import * as qs from 'query-string';

import { apiUtils, authFetch } from '@src/utils';

export interface MerchantIdentity {
  id: string;
  name: string;
}

export interface SiteIdentity {
  id: string;
  merchantId: string;
  name: string;
}

export interface ManagedMerchant extends MerchantIdentity {
  sites: SiteIdentity[];
}

interface Page<T> {
  items: T[];
  nextCursor?: string;
}

const API_URL = apiUtils.getApiUrl();

async function listPages<T>(path: string, query: { merchantId?: string } = {}): Promise<T[]> {
  const items: T[] = [];
  const cursors = new Set<string>();
  let cursor: string | undefined;

  do {
    const page: Page<T> = await authFetch(
      `${API_URL}/${path}?${qs.stringify({ ...query, cursor, limit: 100 })}`,
      { method: 'GET' }
    );
    items.push(...page.items);
    cursor = page.nextCursor;
    if (cursor) {
      if (cursors.has(cursor)) {
        throw new Error('Identity API returned a repeated pagination cursor');
      }
      cursors.add(cursor);
    }
  } while (cursor);

  return items;
}

export const listMerchants = () => listPages<MerchantIdentity>('merchants');

export const listSites = (merchantId: string) => listPages<SiteIdentity>('sites', { merchantId });

export async function getMerchant(merchantId: string): Promise<ManagedMerchant> {
  const [merchant, sites] = await Promise.all([
    authFetch<MerchantIdentity>(`${API_URL}/merchants/${encodeURIComponent(merchantId)}`, {
      method: 'GET',
    }),
    listSites(merchantId),
  ]);

  return { ...merchant, sites };
}

export async function listMerchantsWithSites(): Promise<ManagedMerchant[]> {
  const merchants = await listMerchants();
  const result: ManagedMerchant[] = [];

  // The management overview shows site names. Bound the per-merchant requests
  // rather than sending one request for every merchant at once.
  for (let offset = 0; offset < merchants.length; offset += 5) {
    const batch = await Promise.all(
      merchants.slice(offset, offset + 5).map(async merchant => ({
        ...merchant,
        sites: await listSites(merchant.id),
      }))
    );
    result.push(...batch);
  }

  return result;
}

export const renameMerchant = (id: string, name: string) =>
  authFetch<MerchantIdentity>(
    `${API_URL}/merchants/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/merge-patch+json' },
      body: JSON.stringify({ name }),
    },
    false
  );

export const renameSite = (id: string, name: string) =>
  authFetch<SiteIdentity>(
    `${API_URL}/sites/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/merge-patch+json' },
      body: JSON.stringify({ name }),
    },
    false
  );
